import React, { useEffect, useState, useMemo } from 'react';
import { Modal, Form, Input, Select, Upload, Button, Checkbox, InputNumber, DatePicker, Spin, Collapse, message, Tooltip } from 'antd';
import { UploadOutlined, InfoCircleOutlined, FileOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import {
    useCreateDataSourceMutation,
    useUpdateDataSourceMutation,
    useUploadPortalFileMutation,
    useInspectFileMutation,
} from '../../../redux/api/compassPortal';
import FilePreview from '../FilePreview';

const SectionLabel = ({ children, flag, info }) => (
    <div className={`cp-modal-section__label ${flag ? 'cp-modal-section__label--flag' : ''}`} style={{ marginBottom: 8, marginTop: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
        {children}
        {info && (
            <Tooltip title={info}>
                <InfoCircleOutlined style={{ fontSize: 12, color: 'var(--medium-light-grey-color)', cursor: 'help' }} />
            </Tooltip>
        )}
    </div>
);


const DataSourceModal = ({ open, onClose, projectId, groups, editingSource, metadataSchema = [] }) => {
    const [form] = Form.useForm();
    const [createSource, { isLoading: isCreating }] = useCreateDataSourceMutation();
    const [updateSource, { isLoading: isUpdating }] = useUpdateDataSourceMutation();
    const [uploadFile, { isLoading: isUploading }] = useUploadPortalFileMutation();
    const [inspectFile, { isLoading: isInspecting }] = useInspectFileMutation();
    const token = useSelector((state) => state.oauth.auth0Token);

    const [inspection, setInspection] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);

    const isEditing = !!editingSource;

    // Extract column options from file inspection
    const fileColumns = useMemo(() => {
        if (!inspection?.sheets?.length) return [];
        const cols = inspection.sheets[0].columns || [];
        return cols.map((c) => ({ label: c, value: c }));
    }, [inspection]);

    const dateColumns = useMemo(() => {
        if (!inspection?.sheets?.length) return [];
        const dateCols = inspection.sheets[0].date_columns || {};
        return Object.entries(dateCols).map(([col, range]) => ({
            label: `${col} (${range.min} to ${range.max})`,
            value: col,
        }));
    }, [inspection]);

    useEffect(() => {
        if (!open) return;

        // Small delay to let destroyOnClose recreate the form DOM
        const timer = setTimeout(() => {
            setInspection(null);
            setSelectedFile(null);
            if (editingSource) {
                const md = editingSource.metadata || {};

                // Split comma-separated strings into arrays for multi-select fields
                const metricsRaw = editingSource.metrics?.open_value || '';
                const breakdownRaw = editingSource.breakdown || '';
                const metricsArr = metricsRaw ? metricsRaw.split(',').map(s => s.trim()).filter(Boolean) : [];
                const breakdownArr = breakdownRaw ? breakdownRaw.split(',').map(s => s.trim()).filter(Boolean) : [];

                const formValues = {
                    name: editingSource.name,
                    group_id: editingSource.group_id,
                    upload_method: editingSource.upload_method || 'file',
                    metrics_open: metricsArr.length > 0 ? metricsArr : metricsRaw,
                    breakdown: breakdownArr.length > 0 ? breakdownArr : breakdownRaw,
                    date_column: editingSource.date_column || undefined,
                    notes: editingSource.notes,
                    has_flags: !!editingSource.flags,
                    flags: editingSource.flags,
                };
                // Set schema metadata values
                metadataSchema.forEach((field) => {
                    const val = md[field.key];
                    if (field.type === 'date' && val) {
                        formValues[`meta_${field.key}`] = dayjs(val);
                    } else {
                        formValues[`meta_${field.key}`] = val || undefined;
                    }
                });
                form.setFieldsValue(formValues);

                // Re-inspect the existing file to show preview and populate column dropdowns
                if (editingSource.file_url && editingSource.file_name) {
                    (async () => {
                        try {
                            const res = await fetch(
                                `${process.env.REACT_APP_API_URL || ''}/compass_portal/projects/${projectId}/sources/${editingSource.source_id}/download`,
                                { headers: { 'Authorization': `Bearer ${token}` } },
                            );
                            if (res.ok) {
                                const blob = await res.blob();
                                const file = new File([blob], editingSource.file_name);
                                const result = await inspectFile({ projectId, file }).unwrap();
                                setInspection(result);
                            }
                        } catch {
                            // Non-critical — preview just won't show
                        }
                    })();
                }
            }
        }, 50);
        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editingSource, open, form, metadataSchema, projectId]);

    const handleFileChange = async (info) => {
        const file = info.fileList?.[0]?.originFileObj;
        if (!file) {
            setInspection(null);
            setSelectedFile(null);
            return;
        }
        setSelectedFile(file);
        try {
            const result = await inspectFile({ projectId, file }).unwrap();
            setInspection(result);

            // Auto-fill date range from inspection
            if (result.sheets?.length > 0) {
                const firstSheet = result.sheets[0];
                const dateEntries = Object.entries(firstSheet.date_columns || {});
                if (dateEntries.length > 0) {
                    const [, range] = dateEntries[0];
                    if (range.min) {
                        const startField = metadataSchema.find((f) => f.key.includes('date') && f.key.includes('start'));
                        const endField = metadataSchema.find((f) => f.key.includes('date') && f.key.includes('end'));
                        if (startField) form.setFieldValue(`meta_${startField.key}`, dayjs(range.min));
                        if (endField && range.max) form.setFieldValue(`meta_${endField.key}`, dayjs(range.max));
                    }
                }
            }
        } catch {
            setInspection(null);
        }
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();

            // Build metadata dict
            const metadata = {};
            metadataSchema.forEach((field) => {
                const val = values[`meta_${field.key}`];
                if (val !== undefined && val !== null && val !== '') {
                    metadata[field.key] = field.type === 'date' ? val.format('YYYY-MM-DD') : val;
                }
            });

            // Convert arrays to comma-separated strings for storage
            const metricsValue = Array.isArray(values.metrics_open)
                ? values.metrics_open.join(', ')
                : (values.metrics_open || '');
            const breakdownValue = Array.isArray(values.breakdown)
                ? values.breakdown.join(', ')
                : (values.breakdown || '');

            const sourceData = {
                name: values.name,
                group_id: values.group_id,
                upload_method: values.upload_method || 'file',
                metrics: { open_value: metricsValue },
                breakdown: breakdownValue,
                date_column: values.date_column || '',
                notes: values.notes || '',
                flags: values.has_flags ? (values.flags || 'Flagged') : '',
                metadata,
            };

            let sourceId;
            if (isEditing) {
                await updateSource({ projectId, sourceId: editingSource.source_id, ...sourceData }).unwrap();
                sourceId = editingSource.source_id;
            } else {
                const result = await createSource({ projectId, ...sourceData }).unwrap();
                sourceId = result.source_id;
            }

            if (selectedFile) {
                try {
                    await uploadFile({ projectId, sourceId, file: selectedFile }).unwrap();
                } catch (uploadErr) {
                    message.error('Source created but file upload failed. You can re-upload via Edit.');
                    console.error('File upload failed:', uploadErr);
                }
            }

            message.success(isEditing ? 'Data source updated' : `Data source "${values.name}" created`);
            form.resetFields();
            onClose();
        } catch (err) {
            if (err?.errorFields) return;
            message.error('Failed to save data source');
            console.error('Source save failed:', err);
        }
    };

    const renderMetadataField = (field) => {
        const name = `meta_${field.key}`;
        const rules = field.required ? [{ required: true, message: `${field.label} is required` }] : [];

        switch (field.type) {
            case 'dropdown':
                return (
                    <Form.Item key={name} name={name} label={field.label} rules={rules}>
                        <Select placeholder={`Select ${field.label}`} allowClear>
                            {(field.options || []).map((opt) => (
                                <Select.Option key={opt} value={opt}>{opt}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                );
            case 'date':
                return (
                    <Form.Item key={name} name={name} label={field.label} rules={rules}>
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                );
            case 'number':
                return (
                    <Form.Item key={name} name={name} label={field.label} rules={rules}>
                        <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                );
            default:
                return (
                    <Form.Item key={name} name={name} label={field.label} rules={rules}>
                        <Input />
                    </Form.Item>
                );
        }
    };

    return (
        <Modal
            title={isEditing ? 'Edit Data Source' : 'Create New Data Source'}
            open={open}
            onOk={handleOk}
            onCancel={onClose}
            confirmLoading={isCreating || isUpdating || isUploading}
            okText={isEditing ? 'Update' : 'Proceed'}
            width={640}
            destroyOnClose
        >
            <Form form={form} layout="vertical" preserve={false} initialValues={{ upload_method: 'file' }}>
                <SectionLabel info="Give this data source a descriptive name and assign it to a data group.">OVERVIEW</SectionLabel>
                <Form.Item name="name" rules={[{ required: true, message: 'Enter a source name' }]} style={{ marginBottom: 8 }}>
                    <Input placeholder="Source Name" />
                </Form.Item>
                <Form.Item name="group_id" rules={[{ required: true, message: 'Select a group' }]}>
                    <Select placeholder="Select group">
                        {(groups || []).map((g) => (
                            <Select.Option key={g.group_id} value={g.group_id}>{g.name}</Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <SectionLabel info="Upload a CSV or Excel file to get started.">UPLOAD METHOD</SectionLabel>
                <Form.Item name="upload_method">
                    <Select>
                        <Select.Option value="file">File Upload</Select.Option>
                        <Select.Option value="api" disabled>API Integration (Coming Soon)</Select.Option>
                    </Select>
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.upload_method !== curr.upload_method}>
                    {({ getFieldValue }) => {
                        if (getFieldValue('upload_method') === 'api') {
                            return null;
                        }
                        return (
                            <>
                                {isEditing && editingSource?.file_name && !selectedFile && (
                                    <div style={{
                                        display: 'flex', alignItems: 'center', gap: 8,
                                        padding: '8px 12px', marginBottom: 8,
                                        background: 'var(--tertiary-color)',
                                        border: '1px solid rgba(255,255,255,0.07)',
                                        borderRadius: 6, fontSize: 13,
                                    }}>
                                        <FileOutlined style={{ color: 'var(--green-color)' }} />
                                        <span>Current file: <strong>{editingSource.file_name}</strong></span>
                                    </div>
                                )}
                                <Form.Item name="file" valuePropName="file">
                                    <Upload
                                        beforeUpload={() => false}
                                        maxCount={1}
                                        onChange={handleFileChange}
                                        accept=".csv,.xlsx,.xls"
                                    >
                                        <Button icon={<UploadOutlined />}>
                                            {isEditing && editingSource?.file_name ? 'Replace file...' : 'Choose file...'}
                                        </Button>
                                    </Upload>
                                </Form.Item>
                            </>
                        );
                    }}
                </Form.Item>

                {isInspecting && (
                    <div style={{ textAlign: 'center', padding: 16 }}>
                        <Spin tip="Inspecting file..." />
                    </div>
                )}
                <FilePreview inspection={inspection} />

                {/* Column selection — visible when file columns available */}
                {(fileColumns.length > 0 || isEditing) && (
                    <>
                        <SectionLabel info="Select the column that contains the date for this data source. Used to determine the date range of the data.">DATE COLUMN</SectionLabel>
                        <Form.Item name="date_column">
                            <Select
                                placeholder={fileColumns.length > 0 ? "Select the date column" : "Upload a file to see columns"}
                                allowClear
                                showSearch
                                options={dateColumns.length > 0 ? dateColumns : fileColumns}
                                disabled={fileColumns.length === 0 && !isEditing}
                            />
                        </Form.Item>

                        <SectionLabel info="Select the columns that contain the metrics you want to track (e.g. Revenue, Spend, Conversions). These are the numeric values that will be analysed.">METRICS INCLUDED</SectionLabel>
                        <Form.Item name="metrics_open">
                            <Select
                                mode={fileColumns.length > 0 ? "multiple" : "tags"}
                                placeholder={fileColumns.length > 0 ? "Select metric columns" : "e.g. Revenue, Sessions, Conversions..."}
                                allowClear
                                showSearch
                                options={fileColumns}
                            />
                        </Form.Item>

                        <SectionLabel info="Select the columns used to break down the data (e.g. Campaign, Region, Channel). These are the dimensions the data will be grouped by.">BREAKDOWN COLUMNS</SectionLabel>
                        <Form.Item name="breakdown">
                            <Select
                                mode={fileColumns.length > 0 ? "multiple" : "tags"}
                                placeholder={fileColumns.length > 0 ? "Select breakdown columns" : "e.g. Campaign, Region, Channel..."}
                                allowClear
                                showSearch
                                options={fileColumns}
                            />
                        </Form.Item>
                    </>
                )}

                {/* Advanced options — collapsed by default */}
                <Collapse
                    size="small"
                    style={{ marginTop: 12 }}
                    defaultActiveKey={isEditing ? ['advanced'] : []}
                    items={[{
                        key: 'advanced',
                        label: 'Additional Options',
                        children: (
                            <>
                                {metadataSchema.length > 0 && (
                                    <>
                                        <SectionLabel>METADATA</SectionLabel>
                                        {metadataSchema.map(renderMetadataField)}
                                    </>
                                )}

                                <SectionLabel info="Any additional context about this data source that the team should know.">NOTES</SectionLabel>
                                <Form.Item name="notes">
                                    <Input.TextArea rows={3} placeholder="Any additional context about this data source" />
                                </Form.Item>

                                <SectionLabel flag info="Flag this data source if there are known issues or caveats.">FLAGS</SectionLabel>
                                <Form.Item name="has_flags" valuePropName="checked">
                                    <Checkbox>Any Flags?</Checkbox>
                                </Form.Item>
                                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.has_flags !== curr.has_flags}>
                                    {({ getFieldValue }) =>
                                        getFieldValue('has_flags') ? (
                                            <Form.Item name="flags">
                                                <Input placeholder="Describe the flag" />
                                            </Form.Item>
                                        ) : null
                                    }
                                </Form.Item>
                            </>
                        ),
                    }]}
                />
            </Form>
        </Modal>
    );
};

export default DataSourceModal;
